/** Connected yDoc */

import { logger } from '@tunasong/models'
import { YjsEditor } from '@tunasong/sync-lib'
import { useEffect } from 'react'
import { Editor } from '../slate-typescript.js'
import type { TunaEditor } from '../plugin-types.js'

/**
 * Origin used for the post-connect shape-corrective normalize. slate-yjs
 * gates broadcast on `isLocalOrigin(origin)` which is `origin === localOrigin`;
 * any value different from the editor's configured `localOrigin` means the
 * resulting Slate ops are applied locally but NOT pushed back into the YDoc.
 * Concrete value doesn't matter — only its identity.
 */
const SILENT_NORMALIZE_ORIGIN = Symbol('tuna/silent-normalize')

/** Connect YDoc to the editor */
export const useYDocConnect = (editor: TunaEditor | null) => {
  // Connect editor in useEffect to comply with concurrent mode requirements.
  useEffect(() => {
    if (!editor) {
      return
    }
    if (YjsEditor.isYjsEditor(editor) === false) {
      throw new Error('useYDocConnected: editor must be a YjsEditor')
    }
    const yjsEditor = editor as unknown as YjsEditor
    YjsEditor.connect(yjsEditor)

    /**
     * GH #1161: a fresh `connect()` populates editor.children from the YDoc,
     * which kicks Slate's normalize pass. Documents persisted in legacy
     * shapes (e.g. `li > text` without `lic`) trigger plate-list's
     * `withNormalizeList` to insert + move ~2 ops per list item — every one
     * of which slate-yjs would otherwise mirror back to the shared YDoc and
     * out over the wire. On a 500-item bullet list that's ~1000 writes
     * stacked onto the network for what is logically a no-op render.
     *
     * Run the shape-correction normalize under a non-localOrigin so the ops
     * land in editor.children but skip the YJS broadcast (slate-yjs gates
     * broadcast on `isLocalOrigin(currentOrigin)`). Each client performs
     * the same correction locally — full convergence — and the YDoc itself
     * is healed by the next legitimate edit.
     */
    try {
      YjsEditor.withOrigin(yjsEditor, SILENT_NORMALIZE_ORIGIN, () => {
        Editor.normalize(yjsEditor as unknown as TunaEditor, { force: true })
      })
    } catch (e) {
      /**
       * GH #1339 / TUNASONG-VX: on a YDoc whose structure has desynced from
       * the Slate mapping (the #1339 family), this heal-normalize can itself
       * throw from inside slate-yjs ("Path doesn't match yText", "cannot
       * descent into non-yText"). Letting it bubble has two consequences: the
       * mount crashes into the nearest ErrorBoundary (white-screening the
       * song), and the effect never returns its cleanup — leaking the YJS
       * connection. Rendering the document un-healed is strictly better:
       * every normalize op that already applied left a valid Slate doc, so
       * partial normalization is safe to render, and normalization re-runs on
       * the next change anyway. Log as an error so the desync telemetry
       * (VM/VX) is preserved without the crash.
       */
      logger.error('useYDocConnect: shape-correction normalize failed; rendering un-healed', e)
    }

    return () => {
      /**
       * TUNASONG-W3: `disconnect()` flushes pending local changes into the
       * YDoc, and on a desynced doc (the #1339 family) slate-yjs can throw
       * "Cannot insert text into non-text node" from inside
       * `flushLocalChanges`. A throwing effect cleanup crashes the app into the
       * nearest ErrorBoundary on unmount/teardown — strictly worse than
       * dropping the un-flushable (already structurally-invalid) local ops.
       * Mirror the connect-side guard above: swallow + log so teardown is safe
       * and the desync telemetry (VM/VX/W3) is preserved.
       */
      try {
        YjsEditor.disconnect(yjsEditor)
      } catch (e) {
        logger.error('useYDocConnect: disconnect/flush failed on teardown; releasing un-healed', e)
      }
    }
  }, [editor])
}
